// V2 版式：timeline-h（前端 DOM 版）——对照后端 v2/layouts/timeline-h.ts 逐坐标移植
// 水平轴线贯穿内容区；节点小方块（首节点 accent，其余墨色）；标签上下交错（偶上奇下，与后端 i%2 一致）。
// 供料 timeline block（title 粗 + desc 小字），缺省降级 bullets。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { isLuxe } from "./luxe";
import { brutalTheme, termTheme, memphisTheme, memphisPalette } from "./themes";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

// 与后端 timeline-h.ts GEO 同源（v2geo.generated.ts 由 deno dump 生成）
const GEO = V2GEO["timeline-h"] as any;

export default function V2TimelineH({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  const m = SPACING.marginX;
  const W = 13.333;
  // luxe：节点改为裸金圆点（accent，全部节点统一——timeline-h.ts 分支）
  const luxe = isLuxe(skin);
  // brutal（043 §4.3）：粗线轴；term（§4.4）：$ 步骤行前缀；memphis（§4.7）：圆角节点 5 色轮用
  const brutalT = brutalTheme(skin);
  const termT = termTheme(skin);
  const mpT = memphisTheme(skin);
  // 前端 Block 类型尚未含 V2 block（slides.ts 待主代理同步），此处按后端 types.ts 结构取用
  const blocks = c.blocks as any[];
  const ti = blocks.findIndex((b) => b.type === "timeline");
  const bi = blocks.findIndex((b) => b.type === "bullets");

  let items: { title: string; desc?: string }[];
  if (ti >= 0) {
    items = blocks[ti].items;
  } else {
    items = (bi >= 0 ? (blocks[bi].items as string[]) : []).map((t) => ({ title: t }));
  }
  const nodes = items.slice(0, GEO.maxNodes);

  const contentW = W - 2 * m;
  const step = nodes.length > 0 ? contentW / nodes.length : contentW;
  const labelW = Math.min(GEO.labelMaxW, step - GEO.labelPad);
  const titlePath = (i: number) => (ti >= 0 ? `blocks.${ti}.items.${i}.title` : bi >= 0 ? `blocks.${bi}.items.${i}` : null);
  const descPath = (i: number) => (ti >= 0 ? `blocks.${ti}.items.${i}.desc` : null);

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "timeline-h")} page={page} total={total} foot={footOf()} />
      {/* 页面大标题（长度分档） */}
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(m), top: px(GEO.titleY), width: px(contentW), height: px(GEO.titleH),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: skin.serifHead ? 700 : 400, lineHeight: 1.1,
        }}
      />
      {nodes.length > 0 && (
        <>
          {/* 水平轴线（brutal 下走 ruleW=3 粗黑线轴——timeline-h.ts:59-63） */}
          <div style={{
            position: "absolute", left: px(m), top: px(GEO.axisY),
            width: px(contentW), height: brutalT ? px(SPACING.hairline * 3) : px(GEO.axisH), background: skin.hairline,
          }} />
          {nodes.map((it, i) => {
            const cx = m + step * (i + 0.5);
            const titleY = i % 2 === 0 ? GEO.aboveTitleY : GEO.belowTitleY;
            const tp = titlePath(i), dp = descPath(i);
            return (
              <div key={i}>
                {/* 节点小方块：首节点 accent，其余墨色（静态装饰；luxe 全部裸金圆点；memphis 圆角节点 5 色轮用） */}
                <div style={{
                  position: "absolute",
                  left: px(cx - GEO.node / 2), top: px(GEO.axisY + GEO.axisH / 2 - GEO.node / 2),
                  width: px(GEO.node), height: px(GEO.node),
                  background: mpT
                    ? memphisPalette(mpT)[(page - 1 + i) % 5]
                    : luxe || i === 0 ? skin.accent : skin.text,
                  borderRadius: luxe ? "50%" : mpT ? px(0.08) : 0,
                }} />
                {/* 节点 title（粗体、居中，上下交错） */}
                {tp ? (
                  <EditableText
                    value={(termT ? "$ " : "") + it.title} path={tp} editable={editable}
                    // 045 #11：term `$ ` 前缀在持久化前剥离，防前缀存进文本叠加
                    onEdit={(p, v) => handlers.onEdit?.(p, termT && v.startsWith("$ ") ? v.slice(2) : v)}
                    onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                    style={{
                      position: "absolute", left: px(cx - labelW / 2), top: px(titleY),
                      width: px(labelW), height: px(0.4),
                      fontSize: pt(skin.scale.body), color: skin.text, fontFamily: skin.fontBody,
                      fontWeight: 700, textAlign: "center",
                    }}
                  />
                ) : (
                  <div style={{
                    position: "absolute", left: px(cx - labelW / 2), top: px(titleY),
                    width: px(labelW), height: px(0.4),
                    fontSize: pt(skin.scale.body), color: skin.text, fontFamily: skin.fontBody,
                    fontWeight: 700, textAlign: "center",
                  }}>{(termT ? "$ " : "") + it.title}</div>
                )}
                {/* 节点 desc（小字，timeline 供料才可编辑） */}
                {(it.desc || (editable && dp)) && (
                  dp ? (
                    <EditableText
                      value={it.desc ?? ""} path={dp} editable={editable} multiline
                      onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                      style={{
                        position: "absolute", left: px(cx - labelW / 2), top: px(titleY + GEO.descOff),
                        width: px(labelW), height: px(GEO.descH),
                        fontSize: pt(skin.scale.caption), color: skin.sub, fontFamily: skin.fontBody,
                        textAlign: "center", lineHeight: 1.15,
                      }}
                    />
                  ) : (
                    <div style={{
                      position: "absolute", left: px(cx - labelW / 2), top: px(titleY + GEO.descOff),
                      width: px(labelW), height: px(GEO.descH),
                      fontSize: pt(skin.scale.caption), color: skin.sub, fontFamily: skin.fontBody,
                      textAlign: "center", lineHeight: 1.15,
                    }}>{it.desc}</div>
                  )
                )}
              </div>
            );
          })}
        </>
      )}
    </>
  );
}
